import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { apiService } from '../../services/apiService';
import type { KeywordProposal, ProposalType } from '../../types/keywords';
import type { DocumentResponse } from '../../types/search';
import { describeError, formatShare } from './utils';

const TYPES: ProposalType[] = ['add', 'merge', 'remove'];

/** Example documents, resolved through the ACL-checked document endpoint. */
function ExampleDocuments({ ids }: { ids: string[] }) {
  const { t } = useTranslation('admin');
  const [documents, setDocuments] = useState<(DocumentResponse | null)[] | null>(null);

  useEffect(() => {
    Promise.allSettled(ids.map((id) => apiService.getDocument(id))).then((results) =>
      setDocuments(results.map((r) => (r.status === 'fulfilled' ? r.value : null)))
    );
  }, [ids]);

  if (!documents) return <CircularProgress size={16} />;
  return (
    <Box component="ul" sx={{ m: 0, pl: 3 }}>
      {documents.map((doc, i) => (
        <li key={ids[i]}>
          {doc ? (
            <Link href={doc.url} target="_blank" rel="noopener noreferrer">{doc.title || doc.id}</Link>
          ) : (
            <Typography variant="body2" color="text.secondary" component="span">
              {t('proposals.notVisible')}
            </Typography>
          )}
        </li>
      ))}
    </Box>
  );
}

function ProposalSummary({ proposal }: { proposal: KeywordProposal }) {
  const { t } = useTranslation('admin');
  const { evidence } = proposal;
  return (
    <Box sx={{ flexGrow: 1 }}>
      {proposal.type === 'add' && proposal.keyword && (
        <>
          <Typography fontWeight={500}>
            {proposal.keyword.labels.en}
            {proposal.keyword.labels.de && ` / ${proposal.keyword.labels.de}`}
          </Typography>
          <Typography variant="body2">{proposal.keyword.definition}</Typography>
        </>
      )}
      {proposal.type === 'remove' && <Typography fontWeight={500}>{proposal.target_id}</Typography>}
      {proposal.type === 'merge' && (
        <Typography fontWeight={500}>{proposal.target_id} → {proposal.merge_into}</Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {t(`proposals.reasons.${proposal.reason}`)}
        {evidence.sample_share != null && ` · ${t('proposals.sampleShare')} ${formatShare(evidence.sample_share)}`}
        {evidence.index_doc_count != null && ` · ${t('proposals.indexCount', { count: evidence.index_doc_count })}`}
        {evidence.index_share != null && ` (${formatShare(evidence.index_share)})`}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
        {Object.entries(evidence.sources).map(([source, count]) => (
          <Chip key={source} size="small" variant="outlined" label={`${source}: ${count}`} />
        ))}
      </Stack>
    </Box>
  );
}

export function ProposalsTab({ onDraftChanged }: { onDraftChanged: () => Promise<void> }) {
  const { t } = useTranslation('admin');
  const [proposals, setProposals] = useState<KeywordProposal[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ severity: 'info' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    setProposals(await apiService.getKeywordProposals('pending'));
    setSelected(new Set());
  }, []);

  useEffect(() => {
    load().catch((err) => setMessage({ severity: 'error', text: describeError(err, t) }));
  }, [load, t]);

  const decide = async (ids: string[], decision: 'accept' | 'reject') => {
    setBusy(true);
    setMessage(null);
    try {
      for (const id of ids) {
        await apiService.decideKeywordProposal(id, decision);
      }
    } catch (err) {
      setMessage({ severity: 'error', text: describeError(err, t) });
    } finally {
      await load().catch(() => undefined);
      if (decision === 'accept') await onDraftChanged().catch(() => undefined);
      setBusy(false);
    }
  };

  const requestProposals = async () => {
    try {
      await apiService.requestKeywordProposals();
      setMessage({ severity: 'info', text: t('proposals.requested') });
    } catch (err) {
      setMessage({ severity: 'error', text: describeError(err, t) });
    }
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (!proposals) return <CircularProgress />;

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          {t('proposals.count', { count: proposals.length })}
        </Typography>
        <Button disabled={busy || selected.size === 0} onClick={() => decide([...selected], 'reject')}>
          {t('proposals.rejectSelected')}
        </Button>
        <Button
          variant="outlined"
          disabled={busy || selected.size === 0}
          onClick={() => decide([...selected], 'accept')}
        >
          {t('proposals.acceptSelected')}
        </Button>
        <Button variant="contained" onClick={requestProposals}>{t('proposals.generate')}</Button>
      </Stack>
      {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}

      {TYPES.map((type) => {
        const group = proposals.filter((p) => p.type === type);
        if (group.length === 0) return null;
        return (
          <Box key={type} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>{t(`proposals.types.${type}`)}</Typography>
            <Stack spacing={1}>
              {group.map((proposal) => (
                <Paper key={proposal.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <Checkbox checked={selected.has(proposal.id)} onChange={() => toggle(proposal.id)} />
                    <ProposalSummary proposal={proposal} />
                    <Stack direction="row" spacing={1}>
                      {proposal.evidence.example_doc_ids.length > 0 && (
                        <Button
                          size="small"
                          onClick={() => setExpanded(expanded === proposal.id ? null : proposal.id)}
                        >
                          {t('proposals.examples')}
                        </Button>
                      )}
                      <Button size="small" disabled={busy} onClick={() => decide([proposal.id], 'reject')}>
                        {t('proposals.reject')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        onClick={() => decide([proposal.id], 'accept')}
                      >
                        {t('proposals.accept')}
                      </Button>
                    </Stack>
                  </Stack>
                  <Collapse in={expanded === proposal.id} unmountOnExit>
                    <Box sx={{ pl: 7, pt: 1 }}>
                      <ExampleDocuments ids={proposal.evidence.example_doc_ids} />
                    </Box>
                  </Collapse>
                </Paper>
              ))}
            </Stack>
          </Box>
        );
      })}
      {proposals.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          {t('proposals.empty')}
        </Typography>
      )}
    </Box>
  );
}
